import { get, includes } from "lodash";
import BaseProxy from "../../base-proxy";
import GetUsersMeProxy from "../users/me";
import getAppCustomEnv from "../../../utilities/get-app-custom-env";
import QueryStringHelper from "@/utilities/query-string-helper";
import { getAppFeatureProp } from "@/utilities/app-feature-util";
import { getCompanyUserByQuestionnaireToken } from "@/utilities/accounting-setup";
import getPlatformConfig from "@/utilities/get-platform-config";

// Constants
import { Constants } from "customer-sdk/constants";

const COMPANY_STATUSES = Constants.COMPANY_STATUSES;
const { SLEEK_DOMAINS } = Constants.SLEEK_DOMAINS;
import Auth0Util from "@/utilities/auth0-util";

/**
 * @class LoginProxy
 * @description Class used for authenticating with the login API.
 * @author Kelvin John Santos
 * @version 1.0
 * @since August 16, 2019
 */

let POST_ONBOARDING_AND_PRE_LIVE_COMPANY_STATUSES = [
  COMPANY_STATUSES.PAID_AND_INCOMPLETE.value,
  COMPANY_STATUSES.PROCESSING_BY_SLEEK.value,
  COMPANY_STATUSES.PROCESSING_INCORP_TRANSFER.value,
  COMPANY_STATUSES.REFERRED_TO_ACRA.value,
  COMPANY_STATUSES.REFERRED_TO_ASIC.value,
  COMPANY_STATUSES.LIVE_POST_INCORPORATION.value,
  COMPANY_STATUSES.AWAITING_COMPANY_DETAIL.value,
];

let translateUserApiResponse = async (user, token) => {
  const queryStringHelper = new QueryStringHelper();
  let appCustomEnv = await getAppCustomEnv();
  let companyUsers = await getUserCompanies(user._id, token);
  const storePromise = await import("../../../store/");
  const store = storePromise.default;
  // for new post payment UI

  let lastAccessedCompanyId = user.last_accessed_company_id;
  let platformConfig = store.state ? get(store.state, "configModule.platformConfig") : null;

  const new_dashboard_onboarding_enabled = getAppFeatureProp(
    platformConfig.cmsAppFeatures,
    "new_dashboard_onboarding_enabled"
  );

  let isNewPostPaymentDashboardEnabled = new_dashboard_onboarding_enabled.enabled;

  //@ Filter companyUsers that have null for company
  companyUsers = companyUsers.filter((companyUser) => companyUser.company != null);

  let accountingQuestionnaireAccessToken = queryStringHelper.get("questionnaire_token");
  if (accountingQuestionnaireAccessToken) {
    const { company } = await getCompanyUserByQuestionnaireToken(accountingQuestionnaireAccessToken);
    if (company) {
      lastAccessedCompanyId = company._id;
    }
  }

  return {
    authToken: token,
    userId: user._id,
    email: user.email,
    phone: user.phone,
    firstName: user.first_name,
    lastName: user.last_name,
    isAdmin: user.profile === "admin",
    isSuperAdmin: user.isSuperAdmin === "admin",
    isItSupport: user.isItSupport,
    isBookkeeping: user.isBookkeeping,
    isBookkeepingAdmin: user.isBookkeepingAdmin,
    isAccountingAdmin: user.isAccountingAdmin,
    isAccounting: user.isAccounting,
    permissions: user.permissions,
    isEmailVerified: typeof user.registered_at !== "undefined",
    authUrls: await buildAuthUrls(),
    postLoginNavigationPath: await getPostLoginPath(user, companyUsers, isNewPostPaymentDashboardEnabled),
    postRegistrationNavigationPath: appCustomEnv.VUE_APP_POST_REGISTRATION_NAVIGATION_PATH,
    lastAccessedCompanyId: lastAccessedCompanyId,
    isPartner: user.is_partner,
    isPartnerAdmin: user.isPartnerAdmin,
    partner: user.partner,
  };
};

let getUserCompanies = async (id, token) => {
  let baseProxy = new BaseProxy();
  let backendUrl = await baseProxy.getBackendUrl();
  let endpointUrl = `${backendUrl}/users/${id}/company-users/`;

  return await baseProxy.getAuthenticatedData(endpointUrl, token);
};

let buildAuthUrls = async () => {
  let appCustomEnv = await getAppCustomEnv();
  let appURL = appCustomEnv.VUE_APP_PLATFORM_APP_URL;
  let signURL = appCustomEnv.VUE_APP_SIGN_WFE_URL;

  return [appURL, signURL];
};

let getPostLoginPath = async (user, companyUsers, isNewPostPaymentDashboardEnabled = false) => {
  let appCustomEnv = await getAppCustomEnv();
  let output = appCustomEnv.VUE_APP_ON_BOARDING_PAGE_PATH;

  if (companyUsers.length > 0) {
    let companyId = getDefaultCompanyId(companyUsers, user);
    let company = getCompanyById(companyUsers, companyId);
    let companyStatus = company ? company.status : null;

    user.last_accessed_company_id = companyId;

    if (companyStatus === COMPANY_STATUSES.DRAFT.value) {
      if (typeof company.expected_external_payment_info === "undefined") {
        output = `${appCustomEnv.VUE_APP_ON_BOARDING_PAGE_PATH}?cid=${companyId}`;
      } else {
        output = isNewPostPaymentDashboardEnabled
          ? appCustomEnv.VUE_APP_INCORPORATION_PAGE_PATH
          : `${appCustomEnv.VUE_APP_PAYMENT_PROCESSING_PAGE_PATH}?cid=${companyId}`;
      }
    }

    if (POST_ONBOARDING_AND_PRE_LIVE_COMPANY_STATUSES.some((status) => status === companyStatus)) {
      output = appCustomEnv.VUE_APP_INCORPORATION_PAGE_PATH;
    }

    if (companyStatus === COMPANY_STATUSES.LIVE.value) {
      if (isNewPostPaymentDashboardEnabled && !company.is_dashboard_launched) {
        output = appCustomEnv.VUE_APP_INCORPORATION_PAGE_PATH;
      } else {
        output = appCustomEnv.VUE_APP_POST_LOGIN_NAVIGATION_PATH;
      }
    }
  }

  return output;
};

let getDefaultCompanyId = (companyUsers, user) => {
  let companyId = user.last_accessed_company_id;

  //@ Check if last accessed company is currently a part of user's companies
  // if (!companyId || !companyUsers.find(companyUser => companyUser.company._id.toString() === companyId)) {
  //   companyId = companyUsers[0].company._id;
  // }

  console.log("DEBUG :: shakehands :: getDefaultCompanyById :: companyId => ", companyId);

  return companyId;
};

let getCompanyById = (companies, id) => {
  let company = companies.find((company) => company.company._id === id);
  if (company) {
    return company.company;
  }
};

let handleAuth0Authentication = async (input, getUserContext) => {
  await Auth0Util.setup();
  try {
    let authResult = await Auth0Util.authenticate(input.email, input.access);
    let authToken = `${authResult.tokenType} ${authResult.accessToken}`;
    console.log("this authResult:: ", authResult);
    return await getUserContext(authToken);
  } catch (err) {
    return Promise.reject(err);
  }
};

let handleSleekAuthentication = async (input, getUserContext) => {
  let baseProxy = new BaseProxy();
  let backendUrl = await baseProxy.getBackendUrl();
  let endpointUrl = `${backendUrl}/users/login-keep`;

  let payload = {
    email: input.email,
    password: input.access,
    country_code: get(input, "country_code", null),
    domain_url: get(input, "domain_url", null),
    login_from: get(input, "login_from", null),
  };

  let response = await baseProxy.postData(endpointUrl, payload);

  if (response && !response.auth_token) {
    return response;
  }
  let token = response.auth_token;
  return await getUserContext(token);
};

export default class LoginProxy {
  /**
   * @async
   * @function execute
   * @description Method used for authenticating users.
   * @param input - Input paramaters.
   * @param {String} input.email - Email that will be used for authentication.
   * @param {String} input.access - Password that will be used for authentication.
   */
  async execute(input = {}) {
    // const storePromise = await import("../../../store/");
    // const store = storePromise.default;
    // let platformConfig = store.state ? get(store.state, "configModule.platformConfig") : null;
    const platformConfig = await getPlatformConfig(window.location.hostname);
    const auth0Config = getAppFeatureProp(platformConfig.cmsAppFeatures, "auth0");
    const appCustomEnv = await getAppCustomEnv();

    if (process.env.NODE_ENV == "development" && auth0Config) {
      console.log("entered development env!");
      auth0Config["enabled"] = appCustomEnv.VUE_APP_IS_AUTH0_ENABLED === "true" ? true : false;
    }

    const domain = window.location.hostname;
    const isSleekDomain = includes(SLEEK_DOMAINS, domain);

    if (auth0Config && auth0Config.enabled && isSleekDomain) {
      return await handleAuth0Authentication(input, this.getUserContext);
    } else {
      return await handleSleekAuthentication(input, this.getUserContext);
    }
  }

  async loginWithSUT(singleUseToken) {
    let baseProxy = new BaseProxy();
    let backendUrl = await baseProxy.getBackendUrl();
    let endpointUrl = `${backendUrl}/users/login-with-sut`;

    let payload = {
      single_use_token: singleUseToken,
    };

    return await baseProxy.postData(endpointUrl, payload);
  }

  async getUserContext(token) {
    let usersMe = new GetUsersMeProxy();
    let response = await usersMe.execute(token);
    let userContext = Object.assign({}, response);

    userContext.data = await translateUserApiResponse(userContext, token);
    return userContext;
  }
}
